import {EventEmitter, Injectable} from '@angular/core';
import {
  AllDocumentsPrintType,
  PaperSize,
  PrintableDocuments,
  PrintSettings,
  PrintSettingsDetails,
  PrintSingleDocument
} from '../models/print';
import {ApiService} from './api.service';
import {DialogButton, LangService, LoaderService} from 'ngx-satoris';
import {
  OperationType,
  PrintStickerData,
  RequestWithDuplicata
} from '../models/request';
import {format} from 'date-fns';
import {enGB} from 'date-fns/locale';
import {PlacesService} from './places.service';
import {getStatusLabelsFromRequest, canPrintSticker as envCanPrintSticker} from '../models/default-filter-strategy';
import {RequestService} from './request.service';
import {FieldRecords} from 'mrz';
import {ScanDocumentService} from './scan-document.service';

declare const window: any;

const splitInfoToTwoLines = (input: string, maxCharsInOneLine: number) => {
  if(input.length > maxCharsInOneLine) {
    const index = input.substring(0, maxCharsInOneLine).lastIndexOf(' ');
    return [input.substring(0, index), input.substring(index + 1)];
  } return [input, ''];
};

export function generatePrinterLabel(labelTemplate: string, title: string, subTitle: string, stickerData: PrintStickerData) {
  const [name1stLine, name2ndLine] = splitInfoToTwoLines(stickerData.name, 26);
  const [status1stLine, status2ndLine] = splitInfoToTwoLines(stickerData.status, 12);
  return labelTemplate.replace('{{title}}', title).replace('{{subTitle}}', subTitle)
    .replace('{{name1stLine}}', name1stLine).replace('{{name2ndLine}}', name2ndLine)
    .replace('{{passportNumber}}', stickerData.passportNumber || '').replace('{{dateOfEntry}}', stickerData.dateOfEntry)
    .replace('{{localTitle}}', stickerData.title)
    .replace('{{status1stLine}}', status1stLine).replace('{{status2ndLine}}', status2ndLine)
    .replace('{{until}}', stickerData.until).replace('{{place}}', stickerData.place)
    .replace('{{qrCode}}', stickerData.qrCode || '');
}

@Injectable({
  providedIn: 'root'
})
export class PrintService {
  printDocumentsOptions: PrintSettings;
  printerList: any;
  printLoaded: EventEmitter<AllDocumentsPrintType> = new EventEmitter<AllDocumentsPrintType>();
  activeSingleDoc: PrintSingleDocument;

  constructor(public request: RequestService,
    private loader: LoaderService,
    private api: ApiService,
    private scanDocument: ScanDocumentService,
    private place: PlacesService,
    private lang: LangService) {
    if(this.api.isElectronApp) {
      this.detectPrinterList();
      this.getPrinterOptions();
    }
  }

  standardPrint() {
    window.print();
  }

  print<T>(docName: AllDocumentsPrintType, data?: T) {
    this.loader.loading(true);
    this.reset();
    setTimeout(() => {
      if(data) this.getDoc(docName).data = data;
      this.getDoc(docName).active = true;
    }, 1000);
  }

  readyToPrint(docName: AllDocumentsPrintType, force = false) {
    this.loader.loading(false);
    if(this.api.isElectronApp) {
      const retryButton = [{
        label: this.lang.transform('global.retry'),
        size: 'sm',
        action: () => this.readyToPrint(docName, true)
      }] as DialogButton[];
      if(this.printDocumentsOptions?.[docName]) {
        const printerFromList = this.printerList.find((printer: any) => printer.value === this.printDocumentsOptions[docName].defaultPrinter);
        const printerStatus = window.electron.addon.printerStatusOffline(printerFromList.name);
        if(printerStatus === PrinterStatus.PRINTER_OFFLINE) {
          return this.loader.loading(true, {type: 'warn', additionalButtons: retryButton, message: this.lang.transform('warnPrinterOffline', {printerName: printerFromList.name, docName: this.lang.transform('DYN.' + docName)}), btnLabel: 'openDialog', hideCloseBtn: true}).then(() => {
            // Nothing to do here, retry is from retryButton
          });
        } else {
          if(!force && printerStatus === PrinterStatus.PRINTER_QUEUE_PENDING) {
            return this.loader.loading(true, {type: 'warn', additionalButtons: retryButton, message: this.lang.transform('warnPrinterHasQueue', {printerName: printerFromList.name, docName: this.lang.transform('DYN.' + docName)}), btnLabel: 'openDialog', hideCloseBtn: true}).then(() => {
              // Nothing to do here, retry is from retryButton
            });
          } else {
            if(this.printDocumentsOptions[docName].paperSize === PaperSize.HalfLetter) {
              this.printDocumentsOptions[docName].paperSize = <any>{width: 215900, height: 139700};
            }
            this.printLoaded.emit(docName);
            return electron.print(this.printDocumentsOptions[docName] as PrintSettingsDetails);
          }
        }
      } else {
        return this.loader.loading(true, {type: 'warn', message: this.lang.transform('warnPrinterNotConfigured', {docName: this.lang.transform('DYN.'+ docName)}), btnLabel: 'openDialog', hideCloseBtn: true}).then((res: any) => {
          if(res) {
            this.printLoaded.emit(docName);
            window.print();
            this.getDoc(docName).active = false;
          }
        });
      }
    } else {
      this.printLoaded.emit(docName);
      const isMobile = typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
      if(isMobile) {
        this.printInIframe(docName);
      } else {
        window.print();
        this.getDoc(docName).active = false;
      }
    }
  }

  detectPrinterList() {
    if(this.api.isElectronApp) {
      return window.electron.getPrinters().then((res: {name: string, displayName: string, status: number, isDefault: boolean}[]) => {
        this.printerList = res.map(printer => ({
          name: printer.displayName,
          value: printer.name
        }));
      });
    } else {
      return Promise.resolve();
    }
  }

  getDoc(docName: AllDocumentsPrintType) {
    return PrintableDocuments.find((doc) => doc.name === docName);
  }

  private printInIframe(docName: AllDocumentsPrintType) {
    try {
      const container = document.querySelector('app-print');
      const frames = container?.querySelector('.printFrames');
      if(!frames) {
        window.print();
        const doc = this.getDoc(docName);
        if(doc) doc.active = false;
        return;
      }

      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.style.visibility = 'hidden';
      document.body.appendChild(iframe);

      const ifrw = iframe.contentWindow as Window;
      const ifrd = iframe.contentDocument || ifrw.document;

      if(!ifrd.head) {
        const headEl = ifrd.createElement('head');
        ifrd.documentElement.insertBefore(headEl, ifrd.body || null);
      }
      const head = ifrd.head as HTMLHeadElement;
      document.querySelectorAll('link[rel="stylesheet"]').forEach(link => {
        const clone = link.cloneNode(true) as HTMLLinkElement;
        head.appendChild(clone);
      });
      document.querySelectorAll('style').forEach(style => {
        const clone = style.cloneNode(true) as HTMLStyleElement;
        head.appendChild(clone);
      });
      const pageStyle = ifrd.createElement('style');
      pageStyle.textContent = '@page{size:A4;margin:0} html,body{background:#fff}';
      head.appendChild(pageStyle);

      const wrapper = ifrd.createElement('div');
      wrapper.innerHTML = (frames as HTMLElement).outerHTML;
      ifrd.body.appendChild(wrapper);

      const cleanup = () => {
        try {
          document.body.removeChild(iframe); 
        } catch {}
        const doc = this.getDoc(docName);
        if(doc) doc.active = false;
      };

      setTimeout(() => {
        window.addEventListener('afterprint', cleanup, {once: true});
        ifrw.focus();
        ifrw.print();
      }, 300);
    } catch (e) {
      try {
        window.print(); 
      } finally {
        const doc = this.getDoc(docName);
        if(doc) doc.active = false;
      }
    }
  }

  canPrintSticker = (operation: OperationType, request: RequestWithDuplicata): boolean => {
    if(!request || !operation) return false;
    const allowedByEnv = envCanPrintSticker(operation, request, this.scanDocument);
    return this.api.isElectronApp && allowedByEnv && request.usageCount === 0;
  };

  printSticker = (request: RequestWithDuplicata, overrideLocalUsageUntil?: number, entryTime = new Date(), fields?: FieldRecords) => this.place.getPlace(this.api.userPlaceId).then((place) => {
    const isZplOffline = window.electron ? window.electron.addon.printerStatusOffline('ZPL') : true;
    if(isZplOffline) {
      return this.loader.loading(true, {type: 'warn', message: this.lang.transform('warnPrinterZPLOffline')});
    }
    const title = this.lang.transform('sticker.title');
    const subTitle = this.lang.transform('sticker.subTitle');
    const statusLabels = getStatusLabelsFromRequest(request, this);
    const stickerData = {
      name: ((request.metadata?.FirstName || fields?.firstName || '') + ' ' + (request.metadata?.LastName || fields?.lastName || '')).toUpperCase(),
      passportNumber: request.metadata?.PassportNumber?.toUpperCase(),
      dateOfEntry: format(entryTime, 'dd MMM yyyy', {locale: enGB}).toUpperCase(),
      title: statusLabels?.title || 'CONFIRMATION',
      status: statusLabels?.stickerLabel || '',
      until: overrideLocalUsageUntil ? format(new Date(overrideLocalUsageUntil), 'dd MMM yyyy', {locale: enGB}).toUpperCase() : request.localUsageUntil ? format(new Date(request.localUsageUntil), 'dd MMM yyyy', {locale: enGB}).toUpperCase() : request.defaultLocalUsageUntilFromServer ? format(new Date(Date.now() + request.defaultLocalUsageUntilFromServer), 'dd MMM yyyy', {locale: enGB}).toUpperCase() : '',
      place: place.longName.toUpperCase(),
      qrCode: request.qrCode
    } as PrintStickerData;
    return electron.getLabelTemplate().then(labelTemplate => {
      labelTemplate = labelTemplate || window.labelTemplate;
      return electron.printLabel(generatePrinterLabel(labelTemplate, title, subTitle, stickerData));
    });
  });

  setPrintSettings(updatedPrintSettings: PrintSettings) {
    electron.saveConfig(updatedPrintSettings).catch(():any => undefined);
  }

  private getPrinterOptions() {
    electron.getConfig().catch((): any => null).then((config: any): void => { console.log(config); this.printDocumentsOptions = config; });
  }

  private reset(clearData = true) {
    PrintableDocuments.forEach((doc) => {
      doc.active = false;
      if(clearData) {
        doc.data = undefined;
      }
    });
  }
}
